// Modified App.js to include cleanup functionality and Vercel Analytics

import WelcomeBonusToast from "./Components/WelcomeBonusToast";
import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import Home from "./Screens/Home";
import AppShell from "./Components/AppShell";
import RoomsHub from "./Screens/RoomsHub";
import CreateRoom from "./Screens/CreateRoom";
import JoinRoom, { JoinByCode } from "./Screens/JoinRoom";
import PlayGame from "./Screens/PlayGame";
import GameCoordinator from "./Screens/GameFlow";
import { cleanupExpiredGames } from "./Services/cleanUpExpiredGames";
import MaintenanceScreen from "./Screens/Maintainance";
import StatsPage from "./Screens/Dashboard";
import { cleanupManager } from "./Services/clientSideCleanup";
import CleanupEndpoint from "./Screens/CleanupEndpoint";
import { Analytics } from "@vercel/analytics/react";
import MyRooms from "./Screens/MyRooms";
import useCoinSync from "./hooks/useCoinSync";
import PurchaseStatusBanner from "./Components/PurchaseStatusBanner";
import { resumePendingPurchase } from "./Services/purchaseTracker";
import Account from "./Screens/Account";
import LegalPage from "./Screens/LegalPage";
import Pricing from "./Screens/Pricing";
import DeleteAccount from "./Screens/DeleteAccount";
import Support from "./Screens/Support";
import NotFound from "./Screens/NotFound";
import SoloSetup from "./Screens/SoloSetup";
import SoloGame from "./Screens/SoloGame";
import Feedback from "./Screens/Feedback";
import HowToPlay from "./Screens/HowToPlay";
import analytics from "./Services/analyticsService";
import useAnalyticsPageviews from "./hooks/useAnalyticsPageviews";

const NameItRun = lazy(() => import("./Screens/NameItRun"));
const AdminApp = lazy(() => import("./Screens/admin/AdminApp"));

// Set this to true when in maintenance mode
const MAINTENANCE_MODE = false;

function App() {
  useCoinSync();
  useAnalyticsPageviews();

  // A payment interrupted by a refresh or closed tab still gets its answer.
  useEffect(() => {
    resumePendingPurchase();
  }, []);

  // One-time analytics init, before anything else in the app can capture.
  // init() itself no-ops on a second call, so StrictMode's double-invoke in
  // dev is harmless.
  useEffect(() => {
    analytics.init();
  }, []);

  // Add cleanup on app load and periodically
  useEffect(() => {
    // Run cleanup when app starts
    cleanupExpiredGames();

    // Set up interval to run cleanup every 10 minutes
    const cleanupInterval = setInterval(() => {
      cleanupExpiredGames();
    }, 10 * 60 * 1000);

    return () => clearInterval(cleanupInterval);
  }, []);

  // useEffect(() => {
  //   // Run cleanup when app starts
  //   cleanupManager.runCleanupIfNeeded().catch(console.error);

  //   // Also run the existing game cleanup
  //   cleanupExpiredGames();

  //   // Set up interval to run cleanup every 10 minutes
  //   const cleanupInterval = setInterval(() => {
  //     cleanupExpiredGames();
  //   }, 10 * 60 * 1000);

  //   return () => clearInterval(cleanupInterval);
  // }, []);

  if (MAINTENANCE_MODE) {
    return (
      <>
        <Routes>
          <Route path="/maintenance" element={<MaintenanceScreen />} />
          <Route path="*" element={<Navigate to="/maintenance" replace />} />
        </Routes>
        <Analytics />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<Home />} />
          <Route path="/rooms" element={<RoomsHub />} />
          <Route path="/my-rooms" element={<MyRooms />} />
          <Route path="/account" element={<Account />} />
          <Route path="/feedback" element={<Feedback />} />
        </Route>
        <Route path="/create-room" element={<CreateRoom />} />
        <Route path="/join-room" element={<JoinByCode />} />
        <Route path="/join-room/:roomId" element={<JoinRoom />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/play-game" element={<GameCoordinator />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/api/cleanup" element={<CleanupEndpoint />} />
        <Route path="/adminsimi24" element={<Suspense fallback={null}><AdminApp /></Suspense>} />
        <Route path="/privacy-policy" element={<LegalPage page="privacy" />} />
        <Route path="/terms" element={<LegalPage page="terms" />} />
        <Route path="/refund-policy" element={<LegalPage page="refund" />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/support" element={<Support />} />
        <Route path="/public-rooms" element={<Navigate to="/rooms" replace />} />
        <Route path="/solo" element={<SoloSetup />} />
        <Route path="/solo/game" element={<SoloGame />} />
        <Route path="/how-to-play" element={<HowToPlay />} />
        <Route path="/run" element={<Suspense fallback={null}><NameItRun /></Suspense>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <PurchaseStatusBanner />
      <WelcomeBonusToast />
      <Analytics />
    </>
  );
}

export default App;
